import React, { useState, useRef, useEffect } from "react";
import BasicDatePicker from "../../../../../components/Datepicker/Datepicker";
import { Radios } from "../../../../../components/RadioGroup/Radios";
import "./FilterDateTime.scss";
import "../PanelFilter.scss";

export const FilterDateTime = ({
  filterData,
  groupData,
  isGroup,
  returnValues,
  clearAllTrigger,
}) => {
  // Debug: mostrar qué datos estamos recibiendo
  // console.log("FilterDateTime - filterData:", filterData);
  // console.log("FilterDateTime - groupData:", groupData);
  const dateRangeByFullDate = [
    { id: 0, name: "Today" },
    { id: 1, name: "Yesterday" },
    { id: 2, name: "Current Week" },
    { id: 3, name: "Current Quarter" },
    { id: 4, name: "Current Month" },
    { id: 5, name: "Current Year" },
    { id: 6, name: "Last 7 Days" },
    { id: 7, name: "Last 30 Days" },
    { id: 8, name: "Last Week" },
    { id: 9, name: "Last Month" },
    { id: 10, name: "Last Quarter" },
    { id: 11, name: "Last Year" },
    { id: 12, name: "Until Today" },
    { id: 13, name: "Until Yesterday" },
    { id: 14, name: "Until Last 7 Days" },
    { id: 15, name: "Until Last 30 Days" },
    { id: 16, name: "Until Last Week" },
    { id: 17, name: "Until Last Month" },
    { id: 18, name: "Until Last Quarter" },
    { id: 19, name: "Until Last Year" },
    { id: 20, name: "+1 Year" },
    { id: 21, name: "Date Availability" },
  ];

  // Estado para el componente Radios
  const [isRangeBased, setIsRangeBased] = useState("manual");

  // Estados para el dropdown personalizado de auto range
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectedAutoRange, setSelectedAutoRange] = useState(null);
  const dropdownRef = useRef(null);
  const [filterValues, setFilterValues] = useState({
    filterID: filterData?.filter?.id,
    filterCompositions: [],
    filterType: "datetime range",
    selectionTimeMode: "Manual", // Por defecto Manual
  });

  // Estados para los date pickers - dinámico basado en filterCompositions
  const [values, setValues] = useState(() => {
    const compositions = filterData?.filter?.filterCompositions || [];
    const initialValues = {};
    const currentDate = new Date(); // Obtener la fecha actual

    compositions.forEach((comp, index) => {
      // Inicializar con la fecha actual en lugar de null
      initialValues[index] = currentDate;
    });
    return initialValues;
  });

  // Filtrar opciones de auto range basado en la búsqueda
  const filteredDateRanges = dateRangeByFullDate.filter((option) =>
    option.name.toLowerCase().includes(searchValue.toLowerCase())
  );

  // Manejar click fuera del dropdown para cerrarlo
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Escuchar el trigger de clear all desde el padre
  useEffect(() => {
    if (clearAllTrigger > 0) {
      handleClearAll(true); // true indica que viene del padre
    }
  }, [clearAllTrigger]);

  // Actualizar selectionTimeMode cuando cambie isRangeBased (solo actualizar estado, no notificar al padre aquí)
  useEffect(() => {
    setFilterValues((prevValues) => ({
      ...prevValues,
      selectionTimeMode: isRangeBased === "manual" ? "Manual" : "AutoRange",
    }));
  }, [isRangeBased]);

  // Inicializar automáticamente los filtros con las fechas actuales al cargar el componente
  useEffect(() => {
    const compositions = filterData?.filter?.filterCompositions || [];
    if (compositions.length > 0) {
      const currentDate = new Date();
      const initialFilterValues = { ...filterValues };

      compositions.forEach((composition, index) => {
        const action = {
          id: composition.id,
          value: currentDate,
        };

        const indexValue = initialFilterValues.filterCompositions.findIndex(
          (comp) => comp.id === composition.id
        );

        if (indexValue !== -1) {
          initialFilterValues.filterCompositions[indexValue] = action;
        } else {
          initialFilterValues.filterCompositions.push(action);
        }
      });

      setFilterValues(initialFilterValues);

      // Notificar al padre después de un pequeño delay para evitar warnings de React
      setTimeout(() => {
        returnValues(initialFilterValues);
      }, 100);
    }
  }, [filterData?.filter?.id]); // Solo ejecutar cuando cambie el filtro

  // Función para manejar cambios en los radio buttons
  const handleRangeTypeChange = (value) => {
    setIsRangeBased(value);
    // console.log("Range type changed to:", value);

    // Limpiar selecciones cuando cambia el tipo
    if (value === "auto") {
      setSelectedAutoRange(null);
      setSearchValue("");
      setIsDropdownOpen(false);
    } else {
      // Limpiar valores de date pickers si se cambia a manual
      const compositions = filterData?.filter?.filterCompositions || [];
      const clearedValues = {};
      const currentDate = new Date(); // Usar fecha actual en lugar de null
      compositions.forEach((comp, index) => {
        clearedValues[index] = currentDate;
      });
      setValues(clearedValues);
    }
  };

  // Función para manejar selección de opción en dropdown personalizado
  const handleOptionSelect = (option) => {
    setSelectedAutoRange(option);
    setSearchValue(""); // Limpiar el valor de búsqueda en lugar de poner el nombre
    setIsDropdownOpen(false);
    // console.log("Auto range selected:", option);
  };

  // Manejar click en el input
  const handleInputClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
    // Si el dropdown se está abriendo, limpiar el searchValue para mostrar todas las opciones
    if (!isDropdownOpen) {
      setSearchValue("");
    }
  };

  // Manejar cambio en el input de búsqueda
  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
    if (!isDropdownOpen) {
      setIsDropdownOpen(true);
    }
  };

  // Función para limpiar la selección
  const handleClearAutoRange = () => {
    setSelectedAutoRange(null);
    setSearchValue("");
    setIsDropdownOpen(false);
  };

  // Función para limpiar todas las selecciones del filtro
  const handleClearAll = (fromParent = false) => {
    // console.log(
    //   `🧹 FilterDateTime - handleClearAll llamado - fromParent: ${fromParent}`
    // );
    // console.log(`🧹 FilterDateTime - filterID: ${filterData?.filter?.id}`);

    // Limpiar valores de date pickers - usar fecha actual en lugar de null
    const compositions = filterData?.filter?.filterCompositions || [];
    const clearedValues = {};
    const currentDate = new Date(); // Usar fecha actual
    compositions.forEach((comp, index) => {
      clearedValues[index] = currentDate;
    });
    setValues(clearedValues);

    // Limpiar auto range selection
    setSelectedAutoRange(null);
    setSearchValue("");
    setIsDropdownOpen(false);

    // Limpiar estado interno del filtro
    setFilterValues({
      filterID: filterData?.filter?.id,
      filterCompositions: [],
      filterType: "datetime range",
      selectionTimeMode: "Manual", // Resetear a Manual por defecto
    });

    // Solo notificar al padre si no viene del trigger del padre
    if (!fromParent) {
      // console.log(
      //   ` FilterDateTime - Llamando returnValues con cleared: true`
      // );
      returnValues({
        filterID: filterData?.filter?.id,
        filterCompositions: [],
        cleared: true, // Flag para indicar que se limpió
      });
    } else {
      // console.log(
      //   ` FilterDateTime - NO llamando returnValues (viene del padre)`
      // );
    }
  };

  // Función para validar y ajustar fechas
  const validateAndAdjustDates = (newValue, composition, compositions) => {
    // Función auxiliar para convertir a Date si no lo es
    const ensureDate = (value) => {
      if (!value) return null;

      // Si ya es un objeto Date nativo
      if (value instanceof Date) return value;

      // Si es un objeto Day.js o Moment.js (tiene método toDate())
      if (value && typeof value.toDate === "function") {
        return value.toDate();
      }

      // Si es un objeto Day.js o Moment.js (tiene propiedad $d que es Date)
      if (value && value.$d && value.$d instanceof Date) {
        return value.$d;
      }

      // Si es string o number, intentar convertir
      if (typeof value === "string" || typeof value === "number") {
        const date = new Date(value);
        return isNaN(date.getTime()) ? null : date;
      }

      return null;
    };

    // Convertir newValue a Date
    const newDateValue = ensureDate(newValue);
    if (!newDateValue) {
      console.warn("📅 Invalid date value received:", newValue);
      return newValue; // Retornar el valor original si no se puede convertir
    }

    const filterNameLower = composition.filterName?.toLowerCase() || "";

    // Detectar si es Initial Date o Final Date
    const isInitialDate = filterNameLower.includes("initial date");
    const isFinalDate = filterNameLower.includes("final date");

    if (!isInitialDate && !isFinalDate) {
      return newDateValue;
    }

    // Obtener las composiciones ordenadas para encontrar el par Initial/Final
    const orderedCompositions = compositions.sort(
      (a, b) => a.ordering - b.ordering
    );
    // console.log(
    //   "- orderedCompositions:",
    //   orderedCompositions.map((c) => ({
    //     id: c.id,
    //     filterName: c.filterName,
    //     ordering: c.ordering,
    //   }))
    // );

    let initialDateComposition = null;
    let finalDateComposition = null;
    let initialDateIndex = -1;
    let finalDateIndex = -1;

    // Encontrar las composiciones de Initial y Final Date
    orderedCompositions.forEach((comp, index) => {
      const compFilterNameLower = comp.filterName?.toLowerCase() || "";
      if (compFilterNameLower.includes("initial date")) {
        initialDateComposition = comp;
        initialDateIndex = index;
      } else if (compFilterNameLower.includes("final date")) {
        finalDateComposition = comp;
        finalDateIndex = index;
      }
    });

    // Si no encontramos ambas composiciones, no hacer validación
    if (!initialDateComposition || !finalDateComposition) {
      console.log(
        "❌ No se encontraron ambas composiciones, retornando sin validación"
      );
      return newDateValue;
    }

    // Obtener las fechas actuales y convertirlas a Date
    const currentInitialDate = ensureDate(values[initialDateIndex]);
    const currentFinalDate = ensureDate(values[finalDateIndex]);

    if (isInitialDate) {
      console.log("🔄 Procesando cambio de Initial Date");
      // Si estamos cambiando la fecha inicial
      const newInitialDate = newDateValue;

      // Si hay una fecha final y la nueva fecha inicial es mayor, ajustar la fecha final
      if (
        currentFinalDate &&
        newInitialDate &&
        newInitialDate >= currentFinalDate
      ) {
        const sameDay = new Date(newInitialDate);

        // Actualizar la fecha final automáticamente
        setTimeout(() => {
          setValues((prevValues) => ({
            ...prevValues,
            [finalDateIndex]: sameDay,
          }));

          // Actualizar también los filterValues para la fecha final
          const finalAction = {
            id: finalDateComposition.id,
            value: sameDay,
          };

          setFilterValues((prevFilterValues) => {
            const updatedFilterValues = { ...prevFilterValues };
            const finalIndexValue =
              updatedFilterValues.filterCompositions.findIndex(
                (comp) => comp.id === finalDateComposition.id
              );

            if (finalIndexValue !== -1) {
              updatedFilterValues.filterCompositions[finalIndexValue] =
                finalAction;
            } else {
              updatedFilterValues.filterCompositions.push(finalAction);
            }

            // Notificar al padre después de actualizar
            setTimeout(() => {
              returnValues(updatedFilterValues);
            }, 50);

            return updatedFilterValues;
          });
        }, 0);
      }

      return newDateValue;
    } else if (isFinalDate) {
      // Si estamos cambiando la fecha final
      const newFinalDate = newDateValue;

      // Validar que la nueva fecha final no sea menor que la inicial
      if (
        currentInitialDate &&
        newFinalDate &&
        newFinalDate <= currentInitialDate
      ) {
        const sameDay = new Date(currentInitialDate);

        return sameDay; // Retornar la fecha ajustada
      }

      return newDateValue;
    }

    return newDateValue;
  };

  // Función para manejar cambios en los date pickers
  const changeValueDate = (newValue, fieldIndex, composition) => {
    const compositions = filterData?.filter?.filterCompositions || [];

    // Validar y ajustar la fecha si es necesario
    const validatedValue = validateAndAdjustDates(
      newValue,
      composition,
      compositions
    );

    setValues((prevValues) => ({
      ...prevValues,
      [fieldIndex]: validatedValue,
    }));

    const action = {
      id: composition.id,
      value: validatedValue,
    };

    setFilterValues((prevFilterValues) => {
      const updatedFilterValues = { ...prevFilterValues };
      const indexValue = updatedFilterValues.filterCompositions.findIndex(
        (comp) => comp.id === composition.id
      );

      if (indexValue !== -1) {
        updatedFilterValues.filterCompositions[indexValue] = action;
      } else {
        updatedFilterValues.filterCompositions.push(action);
      }

      // Notificar al padre después de que el estado se haya actualizado
      setTimeout(() => {
        returnValues(updatedFilterValues);
      }, 0);

      return updatedFilterValues;
    });
  };

  // Obtener composiciones ordenadas
  const getOrderedCompositions = () => {
    const compositions = filterData?.filter?.filterCompositions || [];
    return compositions.sort((a, b) => a.ordering - b.ordering);
  };

  return (
    <div className="filter-container">
      <div className="filter-item-title">
        {!isGroup && (
          <button className="config-button">
            <span className="icon-more-horizontal"></span>
          </button>
        )}
        <span className="filter-title-text">
          {filterData?.filter?.filterName ||
            filterData?.label ||
            "Date and Time"}
        </span>
        <span style={{ flex: 1 }}></span>
        <span className="clear-all" onClick={() => handleClearAll()}>
          Clear All
        </span>
        {/* <button
                      className="btn-apply-selection"
                      style={{ marginRight: "5px" }}
                    >
                      Apply selection
                    </button> */}
      </div>

      {/* <div className="filter-item-clear-all">
        <span>Clear All</span>
      </div> */}

      <div className="filter-item-options-radios">
        <Radios
          value={isRangeBased}
          name="rangeType"
          items={[
            { value: "manual", label: "Manual" },
            { value: "auto", label: "Auto Range" },
          ]}
          onChange={handleRangeTypeChange}
        />
      </div>

      {/* Mostrar date pickers solo en modo manual */}
      {isRangeBased === "manual" && (
        <div className="filter-item-datePicker">
          {getOrderedCompositions().map((composition, index) => {
            return (
              <BasicDatePicker
                key={index}
                label={composition.filterName || `Date ${index + 1}`}
                onChange={(newValue) => {
                  changeValueDate(newValue, index, composition);
                }}
                isDateTime={false}
                value={values[index]}
                name={`date_${index}`}
              />
            );
          })}
        </div>
      )}

      {/* Mostrar dropdown personalizado solo en modo auto */}
      {isRangeBased === "auto" && (
        <div className="filter-item-auto-range">
          <div className="filter-item-dropdown" ref={dropdownRef}>
            <div className="search-input-container">
              <input
                type="text"
                placeholder={
                  selectedAutoRange
                    ? selectedAutoRange.name
                    : "Search date ranges..."
                }
                className="search-input"
                value={searchValue}
                onChange={handleSearchChange}
                onClick={handleInputClick}
              />
              <span className="icon-search search-icon"></span>
            </div>

            {isDropdownOpen && (
              <div className="dropdown-menu">
                {filteredDateRanges.length > 0 ? (
                  filteredDateRanges.map((option) => (
                    <div
                      key={option.id}
                      className={`dropdown-option ${
                        selectedAutoRange?.id === option.id ? "selected" : ""
                      }`}
                      onClick={() => handleOptionSelect(option)}
                    >
                      <div className="option-label">
                        <span className="option-name">{option.name}</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="dropdown-no-results">
                    No date ranges found
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
